<template>
  <div class="page-container">
    <!-- 搜索区域 -->
    <Card class="search-card" dis-hover>
      <Form inline :model="search" :label-width="120" @submit.native.prevent>
        <FormItem label="订单号">
          <Input 
            v-model.trim="search.order_sn" 
            placeholder="请输入订单号" 
            clearable 
            style="width: 200px"
          />
        </FormItem>
        <FormItem label="第三方单号">
          <Input 
            v-model.trim="search.transaction_id" 
            placeholder="请输入微信交易单号" 
            clearable 
            style="width: 200px"
          />
        </FormItem>
        <FormItem label="支付状态">
          <Select v-model="search.order_status" clearable placeholder="全部" style="width: 140px">
            <Option v-for="item in orderStatusOptions" :key="item.value" :value="item.value">
              {{ item.label }}
            </Option>
          </Select>
        </FormItem>
        <FormItem>
          <Button type="primary" icon="ios-search" @click="handleSearch">查询</Button>
          <Button style="margin-left: 8px" @click="resetSearch">重置</Button>
        </FormItem>
      </Form>
    </Card>

    <!-- 订单列表 -->
    <Card class="table-card" dis-hover>
      <div slot="title" v-if="list.total > 0">
        订单列表（共 {{ list.total }} 条）
      </div>
      <div slot="title" v-else>
        订单列表
      </div>
      <Spin fix v-if="loading" />
      <div v-if="!loading && list.data.length === 0" class="empty-state">
        <Icon type="ios-document-outline" size="64" color="#c5c8ce" />
        <p>暂无订单数据</p>
      </div>
      <Table 
        v-if="!loading && list.data.length > 0"
        :columns="columns" 
        :data="list.data" 
        border 
        stripe
        :row-class-name="getRowClassName"
      >
        <template #orderType="{ row }">
          <Tag :color="orderTypeColor(row.order_type)">{{ orderTypeText(row.order_type) }}</Tag>
        </template>
        <template #orderStatus="{ row }">
          <Tag :color="orderStatusColor(row.order_status)">
            {{ orderStatusText(row.order_status) }}
          </Tag>
        </template>
        <template #grantStatus="{ row }">
          <Tag :color="row.grant_status === 1 ? 'success' : 'warning'">
            {{ row.grant_status === 1 ? '已入账' : '未入账' }}
          </Tag>
        </template>
        <template #points="{ row }">
          <div class="points-info">
            <div class="info-item">
              <span class="info-label">套餐资源：</span>
              <span class="info-value primary">{{ formatNumber(row.resource_points) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">到账资源：</span>
              <span class="info-value success">{{ formatNumber(row.granted_points) }}</span>
            </div>
          </div>
        </template>
        <template #amount="{ row }">
          <div class="amount-info">
            <div class="amount-main">
              <span class="amount-label">金额</span>
              <span class="amount-value">￥{{ formatPrice(row.pay_amount) }}</span>
            </div>
            <div v-if="row.package_price" class="amount-sub">
              套餐价：￥{{ formatPrice(row.package_price) }}
            </div>
          </div>
        </template>
        <template #time="{ row }">
          <div class="time-info">
            <div class="time-item">
              <span class="time-label">下单：</span>
              <span class="time-value">{{ formatTime(row.created_at) }}</span>
            </div>
            <div class="time-item" v-if="row.pay_time">
              <span class="time-label">支付：</span>
              <span class="time-value">{{ formatTime(row.pay_time) }}</span>
            </div>
            <div class="time-item" v-if="row.grant_time">
              <span class="time-label">入账：</span>
              <span class="time-value">{{ formatTime(row.grant_time) }}</span>
            </div>
          </div>
        </template>
      </Table>
      <Page
        v-if="list.total > 0"
        class="pagination"
        :total="list.total"
        :current="search.page"
        :page-size="search.pageSize"
        :page-size-opts="[10, 20, 30, 50]"
        show-total
        show-elevator
        show-sizer
        @on-change="handlePageChange"
        @on-page-size-change="handlePageSizeChange"
      />
    </Card>
  </div>
</template>

<script>
export default {
  name: 'AiPointsOrderList',
  data () {
    return {
      loading: false,
      search: {
        order_sn: '',
        transaction_id: '',
        order_status: '',
        page: 1,
        pageSize: 10
      },
      list: {
        data: [],
        total: 0
      },
      orderStatusOptions: [
        { label: '全部', value: '' },
        { label: '待支付', value: 0 },
        { label: '已支付', value: 1 },
        { label: '已取消', value: 2 },
        { label: '退款中', value: 3 },
        { label: '已退款', value: 4 }
      ],
      columns: [
        { 
          title: '订单号', 
          key: 'order_sn', 
          minWidth: 180,
          render: (h, { row }) => {
            return h('span', {
              style: { fontFamily: 'monospace', fontWeight: 500 }
            }, row.order_sn)
          }
        },
        { 
          title: '第三方单号', 
          key: 'transaction_id', 
          minWidth: 180,
          render: (h, { row }) => {
            return h('span', {
              style: { 
                fontFamily: 'monospace',
                color: row.transaction_id ? '#515a6e' : '#c5c8ce'
              }
            }, row.transaction_id || '-')
          }
        },
        { title: '订单类型', slot: 'orderType', minWidth: 110, align: 'center' },
        { title: '支付状态', slot: 'orderStatus', minWidth: 120, align: 'center' },
        { title: '入账状态', slot: 'grantStatus', minWidth: 120, align: 'center' },
        { 
          title: '套餐名称', 
          key: 'package_name', 
          minWidth: 160
        },
        { title: '资源点', slot: 'points', minWidth: 160 },
        { title: '金额', slot: 'amount', minWidth: 150 },
        { title: '时间信息', slot: 'time', minWidth: 240 }
      ]
    }
  },
  created () {
    this.fetchData()
  },
  methods: {
    fetchData () {
      this.loading = true
      const payload = { search: { ...this.search } }
      this.requestApi('/adm/get_order_list', payload).then(res => {
        this.loading = false
        if (res.status === 1) {
          this.list = res.data || { data: [], total: 0 }
        } else {
          this.list = { data: [], total: 0 }
          this.alertErr(res.msg || '获取订单失败')
        }
      })
    },
    handleSearch () {
      this.search.page = 1
      this.fetchData()
    },
    resetSearch () {
      this.search = {
        order_sn: '',
        transaction_id: '',
        order_status: '',
        page: 1,
        pageSize: this.search.pageSize
      }
      this.fetchData()
    },
    handlePageChange (page) {
      this.search.page = page
      this.fetchData()
    },
    handlePageSizeChange (size) {
      this.search.pageSize = size
      this.handleSearch()
    },
    orderStatusText (status) {
      const map = {
        0: '待支付',
        1: '已支付',
        2: '已取消',
        3: '退款中',
        4: '已退款'
      }
      return map[status] || '未知'
    },
    orderStatusColor (status) {
      const map = {
        0: 'warning',
        1: 'success',
        2: 'default',
        3: 'warning',
        4: 'error'
      }
      return map[status] || 'default'
    },
    orderTypeText (type) {
      const map = {
        1: '付费购买',
        2: '系统赠送',
        3: '人工赠送',
        4: '活动赠送'
      }
      return map[type] || '其他'
    },
    formatPrice (value) {
      if (value === null || value === undefined) return '0.00'
      return Number(value).toFixed(2)
    },
    formatNumber (num) {
      if (num === null || num === undefined) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    formatTime (time) {
      if (!time) return '-'
      return time
    },
    orderTypeColor (type) {
      const map = {
        1: 'blue',
        2: 'green',
        3: 'orange',
        4: 'purple'
      }
      return map[type] || 'default'
    },
    getRowClassName (row) {
      // 根据订单状态添加不同的行样式
      if (row.order_status === 1 && row.grant_status === 1) {
        return 'row-success'
      } else if (row.order_status === 0) {
        return 'row-warning'
      } else if (row.order_status === 2) {
        return 'row-disabled'
      }
      return ''
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 16px;
  background: #f5f7f9;
  min-height: calc(100vh - 64px);
}

/* 搜索卡片 */
.search-card {
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.search-card .ivu-card-body {
  padding: 16px 20px;
}

.search-card .ivu-form-item {
  margin-bottom: 0;
  margin-right: 16px;
}

.search-card .ivu-form-item:last-child {
  margin-right: 0;
}

/* 表格卡片 */
.table-card {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.table-card .ivu-card-body {
  padding: 20px 24px;
}

/* 表格行样式 */
.row-success {
  background-color: #f0f9ff !important;
}

.row-warning {
  background-color: #fff7e6 !important;
}

.row-disabled {
  opacity: 0.6;
}


/* 资源点信息 */
.points-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  align-items: center;
  font-size: 13px;
  margin-bottom: 4px;
}

.info-label {
  color: #808695;
  font-size: 12px;
}

.info-value {
  font-weight: 600;
  font-size: 14px;
}

.info-value.primary {
  color: #2d8cf0;
}

.info-value.success {
  color: #19be6b;
}

/* 金额信息 */
.amount-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.amount-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.amount-label {
  color: #808695;
  font-size: 12px;
}

.amount-value {
  font-size: 18px;
  font-weight: 700;
  color: #ed4014;
}

.amount-sub {
  font-size: 12px;
  color: #808695;
}

/* 时间信息 */
.time-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.time-item {
  display: flex;
  align-items: center;
  font-size: 12px;
  margin-bottom: 4px;
}

.time-label {
  color: #808695;
}

.time-value {
  color: #515a6e;
  font-family: monospace;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #c5c8ce;
}

.empty-state .ivu-icon {
  margin-bottom: 16px;
}

.empty-state p {
  font-size: 14px;
  color: #808695;
}

/* 分页 */
.pagination {
  margin-top: 20px;
  text-align: right;
  padding-top: 16px;
  border-top: 1px solid #e8eaec;
}

/* 表格优化 */
.table-card .ivu-table {
  font-size: 13px;
}

.table-card .ivu-table th {
  background-color: #fafafa;
  font-weight: 600;
  color: #515a6e;
}

.table-card .ivu-table td {
  padding: 16px 12px;
}

/* 响应式优化 */
@media (max-width: 1200px) {
  .search-card .ivu-form-item {
    margin-bottom: 16px;
  }
}

@media (max-width: 768px) {
  .page-container {
    padding: 12px;
  }
  
  .table-card .ivu-card-body {
    padding: 16px;
  }
  
  .pagination {
    text-align: center;
  }
}
</style>

